import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ContextMenuData } from '../context-menu/context-menu.component';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ProfileImageComponent } from '../profile-image/profile-image.component';
import { catchError, EMPTY, filter } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { IconComponent } from '../../icons/icon.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { LocalAuthService, UserService } from '@sso/ld-sso-with-oauth2-oidc';
import { HeaderNotificationComponent } from '../../../features/notifications/components/header-notification/header-notification.component';
import { MainNavigationTriggerComponent } from '../main-navigation/main-navigation-trigger/main-navigation-trigger.component';
import {
  hasRmDemoRole,
  hasRmUserOrAdminRole,
} from '../../../core/enums/role.enum';
import {
  combineLatest,
  distinctUntilChanged,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { RouteConstants } from '../../constants/route-constants';
import { ShowcaseSessionService } from '../../../core/services/showcase-session.service';
import { TextWithIconComponent } from '../text-with-icon/text-with-icon.component';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../confirm-dialog/confirm-dialog.component';
import { Store } from '@ngrx/store';
import { KillSwitchActions } from '../../../features/dashboard/state/kill-switch.actions';
import {
  selectIsEmergencyStopActive,
  selectIsEmergencyStopConfigured,
} from '../../../features/dashboard/state/kill-switch.selectors';

@Component({
  selector: 'app-page-header',
  imports: [
    BreadcrumbComponent,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
    TranslatePipe,
    ProfileImageComponent,
    HeaderNotificationComponent,
    IconComponent,
    AsyncPipe,
    MainNavigationTriggerComponent,
    TextWithIconComponent,
  ],
  templateUrl: './main-header.component.html',
  styleUrl: './main-header.component.scss',
})
export class MainHeaderComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private _localAuthService = inject(LocalAuthService);
  private _userService = inject(UserService);
  private _showcaseSessionService = inject(ShowcaseSessionService);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);
  private store = inject(Store);

  private destroyRef = inject(DestroyRef);

  isAuthenticated$ = this._localAuthService.isAuthenticated$;
  currentUser$ = this.isAuthenticated$.pipe(
    distinctUntilChanged(),
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        return of(null);
      }

      return this._userService.getUserInfo$();
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  hasUserOrAdminRole$ = this.currentUser$.pipe(
    filter((user): user is NonNullable<typeof user> => !!user),
    map((user) => hasRmUserOrAdminRole(user?.info?.roles ?? [])),
  );
  showHeaderNotification$ = this.hasUserOrAdminRole$;
  isShowcase$ = this.currentUser$.pipe(
    map((user) => hasRmDemoRole(user?.info?.roles ?? [])),
  );
  showEmergencyStop$ = combineLatest([
    this.hasUserOrAdminRole$,
    this.store.select(selectIsEmergencyStopConfigured),
  ]).pipe(map(([hasRole, isConfigured]) => hasRole && isConfigured));

  isEmergencyStopActive$ = combineLatest([
    this.hasUserOrAdminRole$,
    this.store.select(selectIsEmergencyStopActive),
  ]).pipe(map(([hasRole, isActive]) => hasRole && isActive));

  headline: string | null = null;
  description: string | null = null;

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
        catchError((_) => EMPTY),
      )
      .subscribe(() => {
        const data = this.extractRouteData();
        this.headline = data.headline;
        this.description = data.description;
      });
  }

  menuData: ContextMenuData[] = [
    // todo include in another release
    // {
    //   labelKey: "settings.profile.label",
    //   action: () => this.router.navigate([RouteConstants.routeSettings, RouteConstants.routeProfile])
    // },
    {
      labelKey: 'navigation.settings.label',
      action: () => this.router.navigate([RouteConstants.routeSettings]),
    },
    {
      labelKey: 'default.logout',
      action: () => this.onLogout(),
    },
  ];

  onLogout(): void {
    this.isShowcase$.pipe(take(1)).subscribe((isShowcase) => {
      if (isShowcase) {
        this._showcaseSessionService
          .endSession()
          .subscribe(() => this._localAuthService.logout());
      } else {
        this._localAuthService.logout();
      }
    });
  }

  onEmergencyStop(): void {
    const dialogData: ConfirmDialogData = {
      title: this.translateService.instant('emergencyStop.dialog.title'),
      body: this.translateService.instant('emergencyStop.dialog.body'),
      hint: this.translateService.instant('emergencyStop.dialog.hint'),
      confirmLabel: this.translateService.instant('emergencyStop.dialog.label'),
      action: 'danger',
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter((result): result is true => result === true),
      )
      .subscribe((_) => {
        this.store.dispatch(KillSwitchActions.public.triggerEmergencyStop());
      });
  }

  private extractRouteData(): {
    headline: string | null;
    description: string | null;
  } {
    let currentRoute: ActivatedRoute | null = this.route;

    while (currentRoute?.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    while (currentRoute) {
      const title = currentRoute.snapshot.data['headline'];
      const description = currentRoute.snapshot.data['description'];
      if (title || description) {
        return {
          headline: title || null,
          description: description || null,
        };
      }
      currentRoute = currentRoute.parent;
    }

    return { headline: null, description: null };
  }
}
